import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, MapPin, LogOut, Clock, ChevronLeft, ChevronRight, Euro, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const getGPSLocation = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

const formatTimer = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatPause = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

const MitarbeiterStempeluhr = () => {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pauseTime, setPauseTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [monthly, setMonthly] = useState(null);
  const actionLock = useRef(false);

  const loadMonthly = useCallback(async () => {
    try {
      const r = await api.get(`/employee/monthly-summary?year=${viewYear}&month=${viewMonth}`);
      setMonthly(r.data);
    } catch (_) { setMonthly(null); }
  }, [viewYear, viewMonth]);

  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  const prevMonth = () => {
    if (viewMonth === 1) { setViewMonth(12); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 12) { setViewMonth(1); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/employee/time-status');
      setStatus(res.data);
      const active = res.data?.active;
      if (active) {
        setIsPaused(!!active.pause_start_at);
      }
    } catch (e) {
      toast.error('Fehler beim Laden');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Live timer
  useEffect(() => {
    if (!status?.active) { setElapsed(0); setPauseTime(0); return; }
    const active = status.active;
    const calcValues = () => {
      const start = new Date(active.clock_in_at).getTime();
      const now = Date.now();
      let totalPause = (active.total_pause_seconds || 0) * 1000;
      if (active.pause_start_at) {
        totalPause += now - new Date(active.pause_start_at).getTime();
      }
      setElapsed(Math.max(0, Math.floor((now - start - totalPause) / 1000)));
      setPauseTime(Math.floor(totalPause / 1000));
    };
    calcValues();
    const iv = setInterval(calcValues, 1000);
    return () => clearInterval(iv);
  }, [status?.active]);

  const handleClockIn = async () => {
    if (actionLock.current) return;
    actionLock.current = true;
    setActionLoading(true);
    toast.info('Standort wird ermittelt...');
    const location = await getGPSLocation();
    try {
      await api.post('/employee/clock-in', { location });
      toast.success('Eingestempelt!');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
    setActionLoading(false);
    setTimeout(() => { actionLock.current = false; }, 3000);
  };

  const handleClockOut = async () => {
    if (actionLock.current) return;
    // Sicherheitsabfrage: schützt vor versehentlichem Ausstempeln (statt Pause zu drücken)
    const confirmed = window.confirm('Möchten Sie sich wirklich AUSSTEMPELN?\n\nWenn Sie nur eine Pause machen möchten, tippen Sie stattdessen auf den gelben PAUSE-Button unten.');
    if (!confirmed) return;
    actionLock.current = true;
    setActionLoading(true);
    toast.info('Standort wird ermittelt...');
    const location = await getGPSLocation();
    try {
      const res = await api.post('/employee/clock-out', { location });
      toast.success(`Ausgestempelt! ${res.data.total_hours?.toFixed(1)}h`);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
    setActionLoading(false);
    setTimeout(() => { actionLock.current = false; }, 3000);
  };

  const handlePause = async () => {
    if (actionLock.current) return;
    actionLock.current = true;
    try {
      const res = await api.post('/employee/toggle-pause', {});
      setIsPaused(res.data.paused);
      toast.success(res.data.message);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
    setTimeout(() => { actionLock.current = false; }, 3000);
  };

  const isActive = !!status?.active;
  const memberName = status?.member_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
  const todayTotal = (status?.today_entries || []).reduce((s, e) => s + (e.total_hours || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" data-testid="stempeluhr-page">
      {/* Top Bar */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1 rounded-full text-xs font-bold ${isActive ? (isPaused ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700') : 'bg-gray-100 text-gray-500'}`} data-testid="status-badge">
            {isActive ? (isPaused ? 'Pause' : 'Aktiv') : 'Nicht aktiv'}
          </div>
          <span className="text-sm text-gray-600">
            Eingeloggt als: <strong>{memberName}</strong>
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={logout} className="text-gray-500" data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-1" /> Abmelden
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        {/* Timer Display (when active) */}
        {isActive && (
          <div className="mb-6 text-center">
            <p className="text-sm text-gray-500 mb-1 uppercase tracking-wide">Arbeitszeit</p>
            <div className="font-mono text-4xl sm:text-5xl font-bold tabular-nums text-gray-900" data-testid="work-timer">
              {formatTimer(elapsed)}
            </div>
          </div>
        )}

        {/* Big Clock Button */}
        <button
          onClick={isActive ? handleClockOut : handleClockIn}
          disabled={actionLoading}
          className={`relative w-56 h-56 sm:w-64 sm:h-64 rounded-full flex items-center justify-center text-white font-bold text-xl sm:text-2xl uppercase tracking-wider shadow-2xl transition-all duration-300 active:scale-95 focus:outline-none ${
            actionLoading ? 'opacity-60 cursor-not-allowed' :
            isActive
              ? 'bg-gradient-to-b from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 shadow-red-300'
              : 'bg-gradient-to-b from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 shadow-green-300'
          }`}
          style={{
            boxShadow: isActive
              ? '0 8px 32px rgba(239, 68, 68, 0.4), inset 0 2px 4px rgba(255,255,255,0.2)'
              : '0 8px 32px rgba(34, 197, 94, 0.4), inset 0 2px 4px rgba(255,255,255,0.2)'
          }}
          data-testid="main-clock-btn"
        >
          {actionLoading ? (
            <Loader2 className="w-10 h-10 animate-spin" />
          ) : isActive ? (
            'AUSSTEMPELN'
          ) : (
            'EINSTEMPELN'
          )}
          {/* Glow ring */}
          {isActive && !isPaused && (
            <span className="absolute inset-0 rounded-full animate-ping opacity-20 bg-red-400" style={{ animationDuration: '2s' }} />
          )}
        </button>

        {/* Pause Button - deutlich größer + mehr Abstand zum großen Ausstempeln-Button, damit man ihn nicht versehentlich vertauscht */}
        {isActive && (
          <button
            onClick={handlePause}
            disabled={actionLoading}
            className={`mt-10 w-56 sm:w-64 px-8 py-5 rounded-2xl font-bold text-xl sm:text-2xl shadow-lg transition-all duration-200 active:scale-95 border-2 ${
              isPaused
                ? 'bg-gradient-to-b from-green-500 to-green-600 text-white shadow-green-200 border-green-700 hover:from-green-600 hover:to-green-700'
                : 'bg-gradient-to-b from-amber-400 to-amber-500 text-gray-900 shadow-amber-200 border-amber-600 hover:from-amber-500 hover:to-amber-600'
            }`}
            data-testid="pause-btn"
          >
            {isPaused ? 'WEITER ARBEITEN' : 'PAUSE'}
          </button>
        )}

        {/* Time Info Cards */}
        <div className="mt-8 w-full max-w-xs">
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Start:</span>
                <span className="font-mono font-semibold text-gray-900" data-testid="start-time">
                  {isActive ? `${status.active.start_time} Uhr` : '---'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Pausen:</span>
                <span className="font-mono font-semibold text-gray-900" data-testid="pause-display">
                  {isActive ? formatPause(pauseTime) : '00:00'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Ende:</span>
                <span className="font-mono font-semibold text-gray-900" data-testid="end-time">
                  ---
                </span>
              </div>
              {isActive && status.active.clock_in_location && (
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> Standort erfasst</span>
                  <a
                    href={`https://www.google.com/maps?q=${status.active.clock_in_location.lat},${status.active.clock_in_location.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Karte
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Today's completed entries */}
        {(status?.today_entries || []).length > 0 && (
          <div className="mt-6 w-full max-w-xs">
            <p className="text-sm text-gray-500 mb-2 font-medium">Heute abgeschlossen:</p>
            <Card>
              <CardContent className="p-3 space-y-2">
                {status.today_entries.map((e, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{e.start_time} - {e.end_time}</span>
                    <span className="font-semibold text-gray-900">{(e.total_hours || 0).toFixed(1)} h</span>
                  </div>
                ))}
                <div className="flex justify-between items-center text-sm pt-2 border-t font-bold">
                  <span className="text-gray-700">Gesamt heute:</span>
                  <span className="text-green-600">{(todayTotal + (isActive ? elapsed / 3600 : 0)).toFixed(1)} h</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Bottom Info */}
      <div className="text-center py-4 text-xs text-gray-400">
        <Clock className="w-3 h-3 inline mr-1" />
        FarbWelt Service - Stempeluhr
      </div>

      {/* ---- Monatsübersicht (nur für den eingeloggten Mitarbeiter) ---- */}
      <div className="px-4 pb-8">
        <div className="max-w-md mx-auto">
          <Card>
            <CardContent className="p-4">
              {/* Monatsnavigation */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-gray-100" data-testid="prev-month-btn">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="text-center">
                  <div className="text-xs text-gray-500 uppercase tracking-wide flex items-center justify-center gap-1">
                    <Calendar className="w-3 h-3" /> Monatsübersicht
                  </div>
                  <div className="font-bold text-gray-900" data-testid="current-month-label">
                    {MONTHS[viewMonth - 1]} {viewYear}
                  </div>
                </div>
                <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-gray-100" data-testid="next-month-btn">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Summary-Kacheln */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-purple-50 rounded-lg p-2 text-center">
                  <div className="text-[10px] text-gray-500 uppercase">Stunden</div>
                  <div className="text-lg font-bold text-purple-600" data-testid="month-hours">
                    {(monthly?.total_hours || 0).toFixed(1)} h
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <div className="text-[10px] text-gray-500 uppercase">Tage</div>
                  <div className="text-lg font-bold text-blue-600">{monthly?.days || 0}</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center">
                  <div className="text-[10px] text-gray-500 uppercase">Verdienst</div>
                  <div className="text-lg font-bold text-amber-600" data-testid="month-earnings">
                    {(monthly?.total_earnings || 0).toFixed(2)} €
                  </div>
                </div>
              </div>

              <div className="text-xs text-center text-gray-500 mb-3">
                <Euro className="w-3 h-3 inline mr-0.5" />
                Stundenlohn: <strong>{(monthly?.hourly_wage || 0).toFixed(2)} €/Std.</strong>
              </div>

              {/* Zeit-Einträge des Monats */}
              {(monthly?.entries || []).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Keine Einträge in diesem Monat</p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {monthly.entries.map((e, i) => {
                    const d = new Date(e.date + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
                    const earn = (e.total_hours || 0) * (monthly.hourly_wage || 0);
                    return (
                      <div key={i} className="flex justify-between items-center text-sm py-1.5 px-2 rounded hover:bg-gray-50">
                        <div>
                          <div className="font-medium text-gray-900">{d}</div>
                          <div className="text-xs text-gray-500">{e.start_time} – {e.end_time || '???'}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-purple-600">{(e.total_hours || 0).toFixed(1)} h</div>
                          {monthly.hourly_wage > 0 && (
                            <div className="text-xs text-amber-600">= {earn.toFixed(2)} €</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MitarbeiterStempeluhr;
